import { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { motion, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import { TechStackIcon } from '../components/TechStackIcon';
import { 
  ArrowLeft, 
  ExternalLink, 
  Mail, 
  CheckCircle, 
  Calendar,
  MessageCircle,
  Shield,
  Users,
  Settings,
  Smartphone,
  X,
  ZoomIn
} from 'lucide-react';
import { ModernStickyHeader } from '../components/ModernStickyHeader';
import { ProjectSlideshow } from '../components/ProjectSlideshow';
import { getCustomImageUrl } from '../utils/imageProxy';
import { supabase } from '../lib/supabase';
import { formatCategory } from '../utils/formatCategory';
import type { Project } from '../types';

export function ProjectDetail() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const isHomePage = location.pathname === '/';
  const [projects, setProjects] = useState<Project[]>([]);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  
  const { scrollY } = useScroll();
  const heroOpacity = useTransform(scrollY, [0, 300], [1, 0.8]);
  const heroScale = useTransform(scrollY, [0, 300], [1, 0.95]);

  const project = projects.find((p: Project) => p.slug === slug);


  const navLinks = [
    { label: 'Home', href: '/' },
    { label: 'Projects', href: '/projects' },
    { label: 'Blog', href: '/blog' },
    { label: 'Contact', href: '/#contact' }
  ];

  // Fetch projects data
  useEffect(() => {
    const fetchProject = async () => {
      try {
        setIsLoading(true);
        const { data, error } = await supabase
          .from('projects')
          .select('*')
          .eq('slug', slug!)
          .single();

        if (error) throw error;
        
        if (data) {
          const mappedProject: Project = {
            ...data,
            slug: data.slug || undefined,
            description: data.description || '',
            category: data.category || 'apps',
            tech_stack: data.tech_stack || [],
            demo_url: data.demo_url || undefined,
            completion_percentage: data.completion_percentage || undefined,
            meta_title: data.meta_title || undefined,
            meta_description: data.meta_description || undefined,
            created_at: data.created_at || undefined,
            updated_at: data.updated_at || undefined,
            key_features: (data.key_features || []).map((f: any) => {
              if (typeof f === 'string') {
                // Check if it contains status information (format: "name|status")
                if (f.includes('|')) {
                  const [name, status] = f.split('|');
                  return { name, description: '', status: status as 'completed' | 'in-progress' | 'planned' };
                }
                // Legacy format - just the name
                return { name: f, description: '', status: 'completed' as const };
              }
              return f;
            }),
            features: (data.key_features || []).map((f: any) => {
              if (typeof f === 'string') {
                // Check if it contains status information (format: "name|status")
                if (f.includes('|')) {
                  const [name, status] = f.split('|');
                  return { id: name, name, status: status as 'completed' | 'in-progress' | 'planned' };
                }
                // Legacy format - just the name
                return { id: f, name: f, status: 'completed' as const };
              }
              return f;
            }),
            status: (data.status || 'concept') as Project['status'],
            featured_image: data.featured_image || null,
            gallery_images: data.gallery_images || [],
            // Load metrics from testimonials field (where they're stored)
            metrics: (data.testimonials as any) || {}
          };
          setProjects([mappedProject]);
        }
      } catch (err) {
        console.error('Error fetching project:', err);
        setProjects([]);
      } finally {
        setIsLoading(false);
      }
    };

    if (slug) {
      fetchProject();
    }
  }, [slug]);

  // Prevent showing "Project Not Found" during navigation
  const [isNavigating, setIsNavigating] = useState(false);
  
  // Detect navigation away from this page
  useEffect(() => {
    // If we're navigating away from this project detail page, set loading state
    if (location.pathname !== `/project/${slug}`) {
      // Add a small delay to prevent flash
      const timer = setTimeout(() => {
        setIsNavigating(true);
      }, 50);
      
      return () => clearTimeout(timer);
    }
  }, [location.pathname, slug]);
  
  const handleBackWithState = () => {
    setIsNavigating(true);
    navigate('/projects', { replace: true });
  };

  // Track scroll progress
  useEffect(() => {
    const handleScroll = () => {
      const totalHeight = document.documentElement.scrollHeight - window.innerHeight;
      const progress = (window.scrollY / totalHeight) * 100;
      setScrollProgress(progress);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Handle image modal
  const openImageModal = (image: string) => {
    // Opening image modal
    if (image && image.trim()) {
      setSelectedImage(image);
      setShowImageModal(true);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
      // Modal state updated
    } else {
      console.error('No valid image URL provided');
    }
  };

  const closeImageModal = () => {
    setShowImageModal(false);
    setSelectedImage(null);
    // Restore body scroll
    document.body.style.overflow = 'unset';
  };

  // Handle escape key for modal
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        closeImageModal();
      }
    };

    if (showImageModal) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [showImageModal]);

  // Show loading skeleton if navigating away or if project not found but still loading
  if (isNavigating || isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-warm-white to-light-gray dark:from-gray-900 dark:to-gray-800">
        <ModernStickyHeader 
        navLinks={navLinks} 
        onNavigationStart={() => setIsNavigating(true)}
      />
        <div className="container mx-auto px-4 pt-32 pb-24">
          <div className="max-w-7xl mx-auto">
            <div className="animate-pulse">
              <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-32 mb-12"></div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
                <div>
                  <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-24 mb-4"></div>
                  <div className="h-16 bg-gray-200 dark:bg-gray-700 rounded mb-8"></div>
                  <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded mb-4"></div>
                  <div className="grid grid-cols-4 gap-4 mb-8">
                    {[...Array(4)].map((_, i) => (
                      <div key={i} className="h-16 bg-gray-200 dark:bg-gray-700 rounded"></div>
                    ))}
                  </div>
                  <div className="h-12 bg-gray-200 dark:bg-gray-700 rounded w-48"></div>
                </div>
                <div className="h-96 bg-gray-200 dark:bg-gray-700 rounded-3xl"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!project && !isNavigating && !isLoading && projects.length > 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-warm-white to-light-gray dark:from-gray-900 dark:to-gray-800">
        <ModernStickyHeader 
        navLinks={navLinks} 
        onNavigationStart={() => setIsNavigating(true)}
      />
        
        {/* Background Elements */}
        <div className="fixed inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-blue-400/20 to-purple-400/20 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-tr from-purple-400/20 to-blue-400/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }}></div>
        </div>

        <div className="relative z-10 container mx-auto px-4 pt-32 pb-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-4xl mx-auto text-center"
          >
            <h1 className="text-6xl font-bold text-gray-900 dark:text-white mb-6">Project Not Found</h1>
            <p className="text-xl text-gray-600 dark:text-gray-300 mb-8">The project you're looking for doesn't exist.</p>
            <motion.button
            onClick={handleBackWithState}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-2xl font-medium hover:shadow-lg transition-all duration-300"
            >
              <ArrowLeft className="w-5 h-5" />
              Back to Projects
            </motion.button>
          </motion.div>
        </div>
      </div>
    );
  }

  const completedFeatures = project?.features?.filter((f: any) => f.status === 'completed').length || 0;
  const totalFeatures = project?.features?.length || 0;
  const progressPercentage = totalFeatures > 0 ? Math.round((completedFeatures / totalFeatures) * 100) : 0;

  // Dynamic metrics based on project stage and actual data
  const getStageMetrics = () => {
    const metrics = project?.metrics || {};
    const status = project?.status || 'concept';
    
    switch (status) {
      case 'concept':
        return {
          primary: { label: 'Research Hours', value: metrics.research_hours || '0' },
          secondary: { label: 'Market Size', value: metrics.market_size || 'TBD' },
          tertiary: { label: 'Team Size', value: metrics.team_size || '1' },
          quaternary: { label: 'Funding Stage', value: metrics.funding_stage || 'Bootstrap' }
        };
      case 'prototype':
        return {
          primary: { label: 'Funding Stage', value: metrics.funding_stage || 'Bootstrap' },
          secondary: { label: 'TAM', value: metrics.tam || 'TBD' },
          tertiary: { label: 'Development Progress', value: `${project?.completion_percentage || 0}%` },
          quaternary: { label: 'User Feedback', value: metrics.user_feedback || 'N/A' }
        };
      case 'mvp':
        return {
          primary: { label: 'Beta Users', value: metrics.beta_users || '0' },
          secondary: { label: 'Features Complete', value: `${project?.key_features?.length || 0}/${project?.key_features?.length || 0}` },
          tertiary: { label: 'Uptime', value: metrics.uptime || '99.9%' },
          quaternary: { label: 'User Engagement', value: metrics.user_engagement || 'N/A' }
        };
      case 'launched':
        return {
          primary: { label: 'Active Users', value: metrics.users || '0' },
          secondary: { label: 'Revenue Status', value: metrics.revenue_status || 'Pre-Revenue' },
          tertiary: { label: 'Customer Satisfaction', value: metrics.customer_satisfaction || 'N/A' },
          quaternary: { label: 'Market Fit', value: metrics.market_fit || 'N/A' }
        };
      case 'scaling':
        return {
          primary: { label: 'Total Users', value: metrics.users || '0' },
          secondary: { label: 'ARR', value: metrics.arr ? `$${metrics.arr}` : 'N/A' },
          tertiary: { label: 'Team Size', value: metrics.team_size || '1' },
          quaternary: { label: 'Market Share', value: metrics.market_share || 'N/A' }
        };
      default:
        return {
          primary: { label: 'Progress', value: `${project?.completion_percentage || 0}%` },
          secondary: { label: 'Features', value: project?.key_features?.length || 0 },
          tertiary: { label: 'Status', value: getStatusDisplay(status) },
          quaternary: { label: 'Category', value: formatCategory(project?.category || 'apps') }
        };
    }
  };

  const stageMetrics = getStageMetrics();

  // Status display with proper formatting
  const getStatusDisplay = (status: string) => {
    switch (status) {
      case 'concept': return 'Concept';
      case 'prototype': return 'Prototype';
      case 'mvp': return 'MVP';
      case 'launched': return 'Launched';
      case 'scaling': return 'Scaling';
      case 'in_development': return 'In Development';
      default: return status.charAt(0).toUpperCase() + status.slice(1);
    }
  };

  // Status color coding
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'concept': return 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400';
      case 'prototype': return 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400';
      case 'mvp': return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400';
      case 'launched': return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400';
      case 'scaling': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400';
      case 'in_development': return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400';
    }
  };

  // Professional feature icons mapping
  const getFeatureIcon = (featureName: string) => {
    const name = featureName.toLowerCase();
    if (name.includes('chat') || name.includes('message')) return MessageCircle;
    if (name.includes('auth') || name.includes('login') || name.includes('user')) return Shield;
    if (name.includes('discussion') || name.includes('community')) return Users;
    if (name.includes('management') || name.includes('admin')) return Settings;
    if (name.includes('mobile') || name.includes('app')) return Smartphone;
    return CheckCircle;
  };


  return (
    <div className="min-h-screen bg-gradient-to-br from-warm-white to-light-gray dark:from-gray-900 dark:to-gray-800">
      <ModernStickyHeader 
        navLinks={navLinks} 
        onNavigationStart={() => setIsNavigating(true)}
      />
      
      {/* Background Elements - Sophisticated color scheme */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 right-20 w-64 h-64 bg-gradient-to-br from-blue-400/15 to-purple-400/15 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 left-20 w-80 h-80 bg-gradient-to-tr from-purple-400/15 to-blue-400/15 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '3s' }}></div>
        <div className="absolute top-1/2 left-1/4 w-48 h-48 bg-gradient-to-r from-blue-300/10 to-purple-300/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '6s' }}></div>
        <div className="absolute top-1/3 right-1/3 w-72 h-72 bg-gradient-to-l from-purple-300/10 to-blue-300/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '9s' }}></div>
          </div>

      {/* Scroll Progress Indicator */}
      <motion.div
        className="fixed top-0 left-0 h-1 bg-gradient-to-r from-blue-500 to-purple-500 z-50"
        style={{ width: `${scrollProgress}%` }}
      />

      <div className="relative z-10">
        {/* Project Hero Section */}
        <motion.section
          style={{ opacity: heroOpacity, scale: heroScale }}
          className="pt-32 pb-20"
        >
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-7xl mx-auto">
              {/* Back Button */}
              <motion.button
              onClick={handleBackWithState}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="inline-flex items-center gap-3 mb-12 px-6 py-3 bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm border border-gray-200 dark:border-gray-700 rounded-2xl text-gray-700 dark:text-gray-300 hover:bg-white dark:hover:bg-gray-800 hover:shadow-lg transition-all duration-300"
              >
                <ArrowLeft className="w-5 h-5" />
                Back to Projects
              </motion.button>

              {/* Hero Content */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
                {/* Left Column - Project Info */}
            <motion.div
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.8 }}
                >
                  {/* Status and Category */}
                  <div className="flex items-center gap-4 mb-6">
                    <span className={`px-4 py-2 rounded-full text-sm font-medium ${getStatusColor(project?.status || 'concept')}`}>
                      {getStatusDisplay(project?.status || 'concept')}
                    </span>
                    <span className="px-4 py-2 bg-gradient-to-r from-orange-100 to-pink-100 dark:from-orange-900/30 dark:to-pink-900/30 text-orange-600 dark:text-orange-400 rounded-full text-sm font-medium">
                      {formatCategory(project?.category || 'apps')}
                    </span>
                  </div>

                  {/* Project Title */}
                  <h1 className="text-5xl md:text-6xl font-light text-gray-900 dark:text-white mb-6 leading-tight">
                  {project?.title || 'Project Title'}
                </h1>

                  {/* Value Proposition */}
                  <div 
                    className="text-xl text-gray-600 dark:text-gray-300 leading-relaxed mb-8 prose prose-lg dark:prose-invert max-w-none"
                    dangerouslySetInnerHTML={{ 
                      __html: project?.description || '<p>Project description</p>' 
                    }}
                  />

                  {/* Stage-Specific Metrics */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-8">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-gray-900 dark:text-white">{stageMetrics.primary.value}</div>
                      <div className="text-sm text-gray-600 dark:text-gray-400">{stageMetrics.primary.label}</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-gray-900 dark:text-white">{stageMetrics.secondary.value}</div>
                      <div className="text-sm text-gray-600 dark:text-gray-400">{stageMetrics.secondary.label}</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-gray-900 dark:text-white">{stageMetrics.tertiary.value}</div>
                      <div className="text-sm text-gray-600 dark:text-gray-400">{stageMetrics.tertiary.label}</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-gray-900 dark:text-white">{stageMetrics.quaternary.value}</div>
                      <div className="text-sm text-gray-600 dark:text-gray-400">{stageMetrics.quaternary.label}</div>
                    </div>
                  </div>

                  {/* Primary CTA */}
                  <div className="flex flex-col sm:flex-row gap-4">
                    {project?.status === 'launched' && project?.demo_url ? (
                      <motion.a
                        href={project?.demo_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        className="flex items-center justify-center gap-3 px-8 py-4 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-2xl font-medium hover:shadow-lg transition-all duration-300"
                      >
                        <ExternalLink className="w-5 h-5" />
                        Try Live App
                      </motion.a>
                    ) : project?.status === 'in_development' ? (
                      <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        className="flex items-center justify-center gap-3 px-8 py-4 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-2xl font-medium hover:shadow-lg transition-all duration-300"
                      >
                        <Mail className="w-5 h-5" />
                        Request Beta Access
                      </motion.button>
                    ) : (
                      <motion.button
                        onClick={(e) => {
                          e.preventDefault();
                          if (isHomePage) {
                            // Scroll to bottom of the page
                            window.scrollTo({
                              top: document.documentElement.scrollHeight,
                              behavior: 'smooth'
                            });
                          } else {
                            // Navigate to home page and scroll to bottom
                            navigate('/#contact', { 
                              replace: false 
                            });
                          }
                        }}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        className="flex items-center justify-center gap-3 px-8 py-4 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-2xl font-medium hover:shadow-lg transition-all duration-300"
                      >
                        <Mail className="w-5 h-5" />
                        Interested? Let's Connect!
                      </motion.button>
                    )}
                  </div>
                </motion.div>

                {/* Right Column - Hero Image */}
                <motion.div
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.8, delay: 0.2 }}
                  className="relative"
                >
                  <div className="bg-gradient-to-br from-white/90 to-gray-50/80 dark:from-gray-800/90 dark:to-gray-900/80 backdrop-blur-sm rounded-3xl p-6 shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-[1.01]">
                    <motion.div
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        // Image clicked
                        if (project?.featured_image) {
                          openImageModal(project.featured_image);
                        }
                      }}
                      className="cursor-pointer relative group"
                    >
                      <div className="rounded-2xl overflow-hidden">
                        <ProjectSlideshow
                          images={[
                            ...(project?.featured_image ? [project.featured_image] : []),
                            ...(project?.gallery_images || []).filter((img): img is string => img !== null && img !== undefined)
                          ]}
                          title={project?.title || 'Project'}
                          autoplay={true}
                          interval={4000}
                          showThumbnails={true}
                          className="w-full h-full"
                        />
                      </div>
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-300 rounded-2xl flex items-center justify-center pointer-events-none">
                        <motion.div
                          initial={{ scale: 0.8, opacity: 0 }}
                          whileHover={{ scale: 1, opacity: 1 }}
                          transition={{ duration: 0.2 }}
                          className="bg-white/90 dark:bg-gray-800/90 p-3 rounded-full shadow-lg"
                        >
                          <ZoomIn className="w-6 h-6 text-gray-700 dark:text-gray-300" />
                        </motion.div>
                      </div>
                    </motion.div>
                  </div>
              </motion.div>
            </div>
            </div>
          </div>
        </motion.section>

        {/* MVP Features Showcase */}
              {project?.features && project?.features.length > 0 && (
          <motion.section
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true, margin: "-100px" }}
            className="py-20"
          >
            <div className="container mx-auto px-4 sm:px-6 lg:px-8">
              <div className="max-w-7xl mx-auto">
                {/* Section Header */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  className="text-center mb-16"
                >
                  <h2 className="text-4xl font-light text-gray-900 dark:text-white mb-4">
                    Core Features
                  </h2>
                  <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
                    Essential functionality that delivers real value to users
                  </p>
                </motion.div>

                {/* Progress Overview */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm border border-gray-200 dark:border-gray-700 rounded-3xl p-8 mb-12"
                >
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-2xl font-medium text-gray-900 dark:text-white">Development Progress</h3>
                    <span className="text-2xl font-bold text-gray-900 dark:text-white">{progressPercentage}%</span>
                </div>
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3">
                    <motion.div
                      initial={{ width: 0 }}
                      whileInView={{ width: `${progressPercentage}%` }}
                      viewport={{ once: true }}
                      transition={{ duration: 1, ease: "easeOut" }}
                      className="h-3 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full"
                    />
                </div>
                  <div className="flex justify-between text-sm text-gray-600 dark:text-gray-400 mt-2">
                    <span>{completedFeatures} shipped</span>
                    <span>{totalFeatures - completedFeatures} in progress</span>
              </div>
                </motion.div>

                {/* Features Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {project?.features?.map((feature: any, index: number) => {
                    const FeatureIcon = getFeatureIcon(feature.name);
                    return (
                      <motion.div
                        key={feature.id}
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: index * 0.1 }}
                        whileHover={{ scale: 1.02, y: -5 }}
                        className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm border border-gray-200 dark:border-gray-700 rounded-2xl p-6 hover:shadow-lg transition-all duration-300 group"
                      >
                        <div className="flex items-start gap-4">
                          <motion.div 
                            className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-blue-100 to-purple-100 dark:from-blue-900/30 dark:to-purple-900/30 rounded-xl flex items-center justify-center"
                            whileHover={{ scale: 1.1, rotate: 5 }}
                            transition={{ type: "spring", stiffness: 400, damping: 10 }}
                          >
                            <FeatureIcon className="w-6 h-6 text-blue-600 dark:text-blue-400 group-hover:text-blue-700 dark:group-hover:text-blue-300 transition-colors duration-300" />
                          </motion.div>
                          <div className="flex-1">
                            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                              {feature.name}
                            </h3>
                            <div className="flex items-center gap-2">
                        {feature.status === 'completed' ? (
                                <div className="flex items-center gap-1 text-green-600 dark:text-green-400 group-hover:text-green-700 dark:group-hover:text-green-300 transition-colors duration-300">
                                  <motion.div
                                    className="group-hover:scale-110 group-hover:rotate-12 transition-transform duration-300"
                                    whileHover={{ scale: 1.2, rotate: 15 }}
                                    transition={{ type: "spring", stiffness: 400, damping: 10 }}
                                  >
                                    <CheckCircle className="w-4 h-4" />
                                  </motion.div>
                                  <span className="text-sm font-medium">Completed</span>
                                </div>
                              ) : feature.status === 'in-progress' ? (
                                <div className="flex items-center gap-2 text-yellow-600 dark:text-yellow-400 group-hover:text-yellow-700 dark:group-hover:text-yellow-300 transition-colors duration-300">
                                  <div className="flex gap-1">
                                    <motion.div
                                      className="w-1 h-1 bg-yellow-600 dark:bg-yellow-400 rounded-full"
                                      animate={{ 
                                        scale: [1, 1.2, 1],
                                        opacity: [0.4, 1, 0.4]
                                      }}
                                      transition={{ 
                                        duration: 1.4, 
                                        repeat: Infinity, 
                                        ease: "easeInOut",
                                        delay: 0
                                      }}
                                    />
                                    <motion.div
                                      className="w-1 h-1 bg-yellow-600 dark:bg-yellow-400 rounded-full"
                                      animate={{ 
                                        scale: [1, 1.2, 1],
                                        opacity: [0.4, 1, 0.4]
                                      }}
                                      transition={{ 
                                        duration: 1.4, 
                                        repeat: Infinity, 
                                        ease: "easeInOut",
                                        delay: 0.2
                                      }}
                                    />
                                    <motion.div
                                      className="w-1 h-1 bg-yellow-600 dark:bg-yellow-400 rounded-full"
                                      animate={{ 
                                        scale: [1, 1.2, 1],
                                        opacity: [0.4, 1, 0.4]
                                      }}
                                      transition={{ 
                                        duration: 1.4, 
                                        repeat: Infinity, 
                                        ease: "easeInOut",
                                        delay: 0.4
                                      }}
                                    />
                                  </div>
                                  <span className="text-sm font-medium">In Progress</span>
                                </div>
                              ) : (
                                <div className="flex items-center gap-1 text-red-600 dark:text-red-400 group-hover:text-red-700 dark:group-hover:text-red-300 transition-colors duration-300">
                          <motion.div
                                    className="group-hover:scale-110 group-hover:-translate-y-1 transition-transform duration-300"
                                    whileHover={{ scale: 1.2, y: -3 }}
                                    transition={{ type: "spring", stiffness: 400, damping: 10 }}
                                  >
                                    <Calendar className="w-4 h-4" />
                          </motion.div>
                                  <span className="text-sm font-medium">Planned</span>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
                  </div>
                </div>
          </motion.section>
        )}

        {/* Tech Stack Visualization */}
        {project?.tech_stack && project?.tech_stack.length > 0 && (
          <motion.section
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true, margin: "-100px" }}
            className="py-20"
          >
            <div className="container mx-auto px-4 sm:px-6 lg:px-8">
              <div className="max-w-7xl mx-auto">
                {/* Section Header */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  className="text-center mb-16"
                >
                  <h2 className="text-4xl font-light text-gray-900 dark:text-white mb-4">
                    Technical Stack
                </h2>
                  <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
                    Modern technologies chosen for performance, scalability, and developer experience
                  </p>
                </motion.div>

                {/* Tech Stack Grid */}
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                  {project?.tech_stack?.map((tech: string, index: number) => {
                    return (
                      <motion.div
                        key={tech}
                        initial={{ opacity: 0, scale: 0.8 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true }}
                        transition={{ delay: index * 0.1 }}
                        whileHover={{ 
                          y: -6,
                          scale: 1.02,
                          transition: { 
                            type: "spring", 
                            stiffness: 400, 
                            damping: 25 
                          }
                        }}
                        className="relative bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm border border-gray-200/50 dark:border-gray-700/50 rounded-2xl p-6 text-center cursor-pointer group overflow-hidden
                                 hover:shadow-[0_12px_40px_rgba(0,0,0,0.08)] hover:border-blue-200/60 dark:hover:border-blue-400/30
                                 hover:backdrop-blur-md transition-all duration-300 ease-out"
                      >
                        {/* Subtle gradient overlay on hover */}
                        <motion.div
                          className="absolute inset-0 bg-gradient-to-br from-blue-50/30 via-transparent to-purple-50/20 dark:from-blue-900/10 dark:via-transparent dark:to-purple-900/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                          initial={false}
                        />
                        
                        <div className="relative flex flex-col items-center">
                          <motion.div
                            whileHover={{ 
                              scale: 1.1,
                              rotate: [0, -2, 2, 0],
                              transition: { 
                                scale: { type: "spring", stiffness: 300, damping: 20 },
                                rotate: { duration: 0.6, ease: "easeInOut" }
                              }
                            }}
                            className="mb-3 group-hover:drop-shadow-[0_4px_8px_rgba(59,130,246,0.25)] transition-all duration-300"
                          >
                            <TechStackIcon 
                              tech={tech} 
                              size={32} 
                              className="text-gray-700 dark:text-gray-300" 
                            />
                          </motion.div>
                          
                          <motion.div 
                            className="font-medium text-slate-800 dark:text-slate-200 group-hover:text-slate-900 dark:group-hover:text-slate-100 transition-colors duration-200"
                            whileHover={{
                              textShadow: "0 2px 4px rgba(0,0,0,0.1)"
                            }}
                          >
                            {tech}
                          </motion.div>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
          </div>
        </div>
          </motion.section>
        )}
      </div>

      {/* Enhanced Image Modal */}
      <AnimatePresence>
        {showImageModal && selectedImage && (
          <div
            className="fixed inset-0 bg-black/95 backdrop-blur-sm z-[9999] flex items-center justify-center p-4"
            style={{ 
              position: 'fixed', 
              top: 0, 
              left: 0, 
              width: '100vw',
              height: '100vh',
              zIndex: 9999,
              margin: 0,
              padding: 0
            }}
            onClick={closeImageModal}
          >
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 flex items-center justify-center p-8"
              onClick={closeImageModal}
            >
              {/* Close Button */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  closeImageModal();
                }}
                className="absolute top-6 right-6 z-30 p-3 bg-white/10 hover:bg-white/20 text-white rounded-full transition-all duration-200 hover:scale-110 backdrop-blur-sm"
              >
                <X className="w-6 h-6" />
              </button>
              
              {/* Image Container */}
              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.8, opacity: 0 }}
                transition={{ type: "spring", stiffness: 300, damping: 30 }}
                className="flex items-center justify-center w-full h-full"
                onClick={(e) => e.stopPropagation()}
              >
                <img
                  src={getCustomImageUrl(selectedImage, { width: 1200, quality: 90, format: 'webp' })}
                  alt={project?.title || 'Project'}
                  className="max-w-[90vw] max-h-[90vh] object-contain rounded-lg"
                  style={{
                    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)'
                  }}
                  onLoad={() => {}}
                  onError={(e) => console.error('Image failed to load:', e)}
                />
              </motion.div>
              
              {/* Image Info */}
              <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 bg-black/50 backdrop-blur-sm text-white p-4 rounded-xl max-w-md">
                <h3 className="font-medium text-lg text-center">{project?.title || 'Project Image'}</h3>
                <p className="text-sm text-gray-300 mt-1 text-center">Click outside or press ESC to close</p>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}